import { Link, useParams } from 'react-router-dom';
import { usePageMeta } from '@/hooks/usePageMeta';
import { getVideoById, getRelatedVideos, formatDate, formatViews } from '@/utils/content';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';
import VideoPlayer from '@/components/feature/VideoPlayer';
import ShareButtons from '@/components/feature/ShareButtons';

const container = 'w-full max-w-[1280px] mx-auto px-4 md:px-8';

export default function VideoDetailPage() {
  const { id = '' } = useParams();
  const item = getVideoById(id);

  usePageMeta(
    item ? `${item.title} - ${item.typeName}视频 - 91.con` : '视频详情 - 91.con',
    item ? item.description : '91.con 娱乐视频详情页',
    item ? `${item.tags.join(',')},${item.typeName},91.con,娱乐视频` : '娱乐视频,91.con'
  );

  if (!item) {
    return (
      <section className={`${container} py-20`}>
        <div className="text-center max-w-lg mx-auto">
          <i className="ri-movie-2-line text-5xl text-foreground-500" />
          <h1 className="font-heading text-2xl font-bold text-foreground-950 mt-4">未找到该视频</h1>
          <p className="text-sm text-foreground-600 mt-2">视频可能已下线或链接有误，去视频专区看看其他内容吧。</p>
          <Link
            to="/videos"
            className="mt-6 inline-flex h-11 px-6 items-center rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            前往视频专区
          </Link>
        </div>
      </section>
    );
  }

  const related = getRelatedVideos(item, 4);
  const sidebar = getRelatedVideos(item, 5);

  return (
    <>
      <section className="w-full bg-background-100 border-b border-background-200">
        <div className={`${container} py-6`}>
          <Breadcrumb items={[{ label: '视频资源', to: '/videos' }, { label: item.typeName }, { label: '播放' }]} />
        </div>
      </section>

      <section className={`${container} py-10 md:py-12`}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-10">
          <div className="lg:col-span-2">
            <VideoPlayer cover={item.cover} title={item.title} duration={item.duration} />

            <h1 className="font-heading text-xl md:text-3xl font-extrabold text-foreground-950 mt-6 leading-snug">
              {item.title}
            </h1>

            <div className="flex items-center flex-wrap gap-4 mt-4 pb-5 border-b border-background-200 text-xs text-foreground-500">
              <span className="px-2 py-0.5 rounded-md bg-accent-500/15 border border-accent-500/40 text-accent-400 font-medium">
                {item.typeName}
              </span>
              <span className="flex items-center gap-1.5"><i className="ri-time-line" />时长 {item.duration}</span>
              <span className="flex items-center gap-1.5"><i className="ri-calendar-line" />上传于 {formatDate(item.uploadDate)}</span>
              <span className="flex items-center gap-1.5"><i className="ri-eye-line" />{formatViews(item.views)} 次播放</span>
            </div>

            <div className="mt-5">
              <h2 className="font-heading text-base font-bold text-foreground-950 mb-2">视频简介</h2>
              <p className="text-base text-foreground-700 leading-loose">{item.description}</p>
              <div className="flex flex-wrap items-center gap-2 mt-4">
                {item.tags.map((t) => (
                  <Link
                    key={t}
                    to={`/search?q=${encodeURIComponent(t)}`}
                    className="px-3 py-1.5 rounded-full bg-secondary-100 border border-background-300 text-secondary-900 text-xs hover:border-accent-500/60 transition-colors cursor-pointer"
                  >
                    #{t}
                  </Link>
                ))}
              </div>
            </div>

            <div className="mt-7">
              <ShareButtons title={item.title} />
            </div>
          </div>

          <aside>
            <h2 className="font-heading text-lg font-bold text-foreground-950 flex items-center gap-2 mb-4">
              <span className="w-1 h-5 rounded-full bg-primary-500" />
              相关推荐
            </h2>
            <div className="flex flex-col gap-3">
              {sidebar.map((v) => (
                <Link
                  key={v.id}
                  to={`/video/${v.id}`}
                  className="group flex gap-3 p-3 rounded-lg bg-background-100 border border-background-200 hover:border-primary-500/60 transition-colors cursor-pointer"
                >
                  <div className="relative w-28 h-16 rounded-md overflow-hidden shrink-0">
                    <img src={v.cover} alt={v.title} title={v.title} className="w-full h-full object-cover object-top" />
                    <span className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded bg-background-50/85 text-foreground-950 text-[10px]">
                      {v.duration}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-400 transition-colors line-clamp-2 leading-snug">
                      {v.title}
                    </h3>
                    <span className="text-xs text-foreground-500 mt-1 inline-block">{v.typeName} · {formatViews(v.views)} 播放</span>
                  </div>
                </Link>
              ))}
            </div>
          </aside>
        </div>

        <div className="mt-14">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 flex items-center gap-2.5 mb-6">
            <span className="w-1 h-6 rounded-full bg-accent-500" />
            更多推荐视频
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {related.map((v) => (
              <VideoCard key={v.id} item={v} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}